import React, { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import { ChatSummaryPanel } from 'features/AskMdt/components/ChatSummaryPanel'
import { useChat } from 'features/Chat/ChatContext'
import useTypingIndicator from 'features/Chat/hooks/useTypingIndicator'
import useReadReceipts from 'features/Chat/hooks/useReadReceipts'
import MessageGroupList from './MessageGroupList'
import Composer from './Composer'

export interface Props {
	/** The popup is open and this view is the one on screen. */
	isActive: boolean
}

const ThreadView = ({ isActive }: Props) => {
	const {
		state: {
			currentEventId,
			currentMessages,
			currentChatHeader,
			readStateByEventId,
			chatInput,
			pendingAttachments,
			isSendingMessage,
			userId,
		},
		actions: {
			handleChatInput,
			handleSendChatMessage,
			handleInsertEmoji,
			handleAddAttachments,
			handleRemoveAttachment,
			loadEarlierMessages,
			retrySendMessage,
			editMessage,
			toggleReaction,
		},
	} = useChat()

	// The name that travels with a typing_start, so the other side can say who.
	const userName = useSelector((state: any) => state.userLogin?.userInfo?.name)
	const [isAtBottom, setIsAtBottom] = useState(true)

	const messages = currentMessages || []
	const isGroup = (currentChatHeader?.receiverIds?.length || 0) > 1
	const readState = readStateByEventId?.[currentEventId] || []

	const { typistNames, notifyTyping, stopTyping } = useTypingIndicator({
		roomId: currentEventId,
		userId,
		name: userName || 'Someone',
		enabled: isActive && !!currentEventId,
	})

	useReadReceipts({
		eventId: currentEventId,
		userId,
		messages,
		isActive,
		isAtBottom,
	})

	const handleSend = useCallback(
		(e: React.FormEvent | React.MouseEvent) => {
			stopTyping()
			handleSendChatMessage(e)
		},
		[stopTyping, handleSendChatMessage],
	)

	return (
		<div data-testid="chat-popup-thread" className="flex min-h-0 flex-1 flex-col">
			<ChatSummaryPanel conversationKey={currentEventId} />
			<MessageGroupList
				messages={messages}
				userId={userId}
				header={currentChatHeader}
				readState={readState}
				isGroup={isGroup}
				typists={typistNames}
				onRetry={retrySendMessage}
				onEdit={editMessage}
				onReact={toggleReaction}
				onLoadEarlier={() => loadEarlierMessages(currentEventId)}
				onScrollStateChange={setIsAtBottom}
			/>

			<Composer
				value={chatInput}
				isDisabled={isSendingMessage}
				pendingAttachments={pendingAttachments}
				onChange={handleChatInput}
				onSend={handleSend}
				onInsertEmoji={handleInsertEmoji}
				onAddAttachments={handleAddAttachments}
				onRemoveAttachment={handleRemoveAttachment}
				onTyping={notifyTyping}
			/>
		</div>
	)
}

export default ThreadView
